import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMatch,
  LinkProps,
  useResolvedPath,
  Link,
  useParams,
  useLocation,
} from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-regular-svg-icons';
import Tippy from '@tippyjs/react';

import { cn } from 'src/common/utils';
import { Badge } from 'src/common/components/Badge';
import { Menu } from 'src/common/components/Menu';
import { useOverflowNav } from 'src/common/hooks/useOverflowNav';
import { useMediaQuery } from 'src/common/hooks/useMediaQuery';
import { getAppbarNarrowPhoneMediaQuery } from './layout';

type NavItem = {
  to: string;
  label: React.ReactNode;
};

export const primaryNavLinkClassName = (isActive: boolean) =>
  cn(
    'flex h-full shrink-0 items-center px-3 text-ugBlue hover:bg-white/10 focus:text-ugBlue focus:outline-none lg:px-6',
    isActive && 'shadow-[inset_0_-2px_0_0_#E0E721]',
  );

function isNavItemActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

export const PrimaryNav = ({
  isDemoProject,
  hasDocumentationViewingPermissions,
  hasAnalysisViewingPermissions,
}: {
  isDemoProject: boolean;
  hasDocumentationViewingPermissions: boolean;
  hasAnalysisViewingPermissions: boolean;
}) => {
  const { projectId = '' } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation(['app', 'common']);
  const isNarrowPhone = useMediaQuery(getAppbarNarrowPhoneMediaQuery());

  const items = useMemo<NavItem[]>(() => {
    const navItems: NavItem[] = [
      {
        to: `/p/${projectId}/dashboard`,
        label: (
          <div className="flex items-center gap-2">
            {t('appbar.project')}
            {isDemoProject && <Badge variant="teal">{t('common:demo')}</Badge>}
          </div>
        ),
      },
      {
        to: `/p/${projectId}/planning`,
        label: t('appbar.planning'),
      },
    ];

    if (hasDocumentationViewingPermissions) {
      navItems.push({
        to: `/p/${projectId}/conrep`,
        label: t('appbar.btb'),
      });
    }

    if (hasAnalysisViewingPermissions) {
      navItems.push({
        to: `/p/${projectId}/a`,
        label: t('appbar.analysis'),
      });
    }

    return navItems;
  }, [
    projectId,
    t,
    isDemoProject,
    hasDocumentationViewingPermissions,
    hasAnalysisViewingPermissions,
  ]);

  const measureKey = `${i18n.language}:${items.length}:${isDemoProject}`;

  const { visibleCount, setContainerEl, setMeasureEl, setOverflowMeasureEl } =
    useOverflowNav({
      itemCount: items.length,
      measureKey,
    });

  const { visibleItems, overflowItems } = useMemo(() => {
    if (isNarrowPhone && items.length > 1) {
      const activeIndex = items.findIndex((item) =>
        isNavItemActive(location.pathname, item.to),
      );
      const resolvedActiveIndex = activeIndex >= 0 ? activeIndex : 0;

      return {
        visibleItems: [items[resolvedActiveIndex]],
        overflowItems: items.filter(
          (_, index) => index !== resolvedActiveIndex,
        ),
      };
    }

    return {
      visibleItems: items.slice(0, visibleCount),
      overflowItems: items.slice(visibleCount),
    };
  }, [isNarrowPhone, items, location.pathname, visibleCount]);

  const hasOverflow = overflowItems.length > 0;

  return (
    <div ref={setContainerEl} className="relative h-full min-w-0 flex-1">
      <div
        ref={setMeasureEl}
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] top-0 flex whitespace-nowrap opacity-0"
      >
        {items.map((item) => (
          <span
            key={item.to}
            className={cn(primaryNavLinkClassName(false), 'inline-flex')}
          >
            {item.label}
          </span>
        ))}
      </div>

      <div
        ref={setOverflowMeasureEl}
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] top-0 opacity-0"
      >
        <span className={cn(primaryNavLinkClassName(false), 'inline-flex')}>
          <FontAwesomeIcon icon={faEllipsisH} fixedWidth />
        </span>
      </div>

      <div className="flex h-full min-w-0">
        {visibleItems.map((item) => (
          <PrimaryNavLink key={item.to} to={item.to}>
            {item.label}
          </PrimaryNavLink>
        ))}
        {hasOverflow && <PrimaryNavOverflowMenu items={overflowItems} />}
      </div>
    </div>
  );
};

const PrimaryNavLink = ({ children, to, ...rest }: LinkProps) => {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: `${resolved.pathname}/*` });

  return (
    <Link to={to} {...rest} className={primaryNavLinkClassName(!!match)}>
      {children}
    </Link>
  );
};

const PrimaryNavOverflowMenu = ({ items }: { items: NavItem[] }) => {
  const { t } = useTranslation('app');
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isOverflowActive = items.some((item) =>
    isNavItemActive(location.pathname, item.to),
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <Tippy
      content={
        <Menu className="text-shuttleGray-800">
          <Menu.List>
            {items.map((item) => (
              <OverflowMenuItem
                key={item.to}
                item={item}
                onSelect={() => setIsOpen(false)}
              />
            ))}
          </Menu.List>
        </Menu>
      }
      interactive
      visible={isOpen}
      onClickOutside={() => setIsOpen(false)}
      onHidden={() => setIsOpen(false)}
      trigger="manual"
      placement="bottom-start"
      arrow={false}
      appendTo={document.body}
      zIndex={60}
    >
      <button
        type="button"
        aria-label={t('appbar.moreSections')}
        aria-expanded={isOpen}
        className={primaryNavLinkClassName(isOverflowActive)}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <FontAwesomeIcon icon={faEllipsisH} fixedWidth />
      </button>
    </Tippy>
  );
};

const OverflowMenuItem = ({
  item,
  onSelect,
}: {
  item: NavItem;
  onSelect: () => void;
}) => {
  const resolved = useResolvedPath(item.to);
  const match = useMatch({ path: `${resolved.pathname}/*` });

  return (
    <Link to={item.to} onClick={onSelect}>
      <Menu.Item selected={!!match}>{item.label}</Menu.Item>
    </Link>
  );
};

/** Single Documentation link for narrow-phone project appbar. */
export const MobileDocumentationNav = () => {
  const { projectId = '' } = useParams();
  const { t } = useTranslation('app');
  const to = `/p/${projectId}/conrep`;
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: `${resolved.pathname}/*` });

  return (
    <Link to={to} className={primaryNavLinkClassName(!!match)}>
      {t('appbar.btb')}
    </Link>
  );
};
